/**
 * Common Endpage Utilities for RespWeb
 */

import { LOCALES, GEO_REGIONS } from './constants';

/**
 * Returns a class name based on the provided variant and suffix.
 * If the variant is not found, it falls back to a specified fallback class.
 *
 * @param {Record<string, string | undefined>} classes - An object mapping class names.
 * @param {string | undefined} variant - The variant to look for.
 * @param {string} suffix - The suffix to append to the variant.
 * @param {string} fallback - The fallback class name if the variant is not found.
 * @returns {string} The class name corresponding to the variant and suffix, or the fallback class.
 */
export function getVariantClass(
  classes: Record<string, string | undefined>,
  variant: string | undefined,
  suffix: string,
  fallback?: string | undefined
): string {
  if (variant && suffix) {
    const variantClass = classes[`${variant}${suffix}`];
    if (variantClass) {
      return variantClass;
    }
  }
  if (fallback && classes[fallback]) {
    return classes[fallback];
  }
  return '';
}

// =============================================================================
// LOCALE & REGION UTILITIES
// =============================================================================

/**
 * Get region for a country code
 * @param countryCode - Two-letter country code
 * @returns Region name or null if not found
 */
export function getRegionForCountry(countryCode: string): string | null {
  const regions = Object.entries(GEO_REGIONS);
  const foundRegion = regions.find(([, countries]) => (countries as readonly string[]).includes(countryCode));
  return foundRegion ? foundRegion[0] : null;
}

/**
 * Check if locale is supported
 * @param locale - Locale code to check
 * @returns True if locale is supported
 */
export function isSupportedLocale(locale: string): boolean {
  return (Object.values(LOCALES) as string[]).includes(locale);
}

/**
 * Get default locale for region
 * @param region - Region name
 * @returns Default locale for region
 */
export function getDefaultLocaleForRegion(region: string): string {
  const regionLocaleMap: Record<string, string> = {
    USA: LOCALES.EN,
    GAC: LOCALES.EN,
    EUROPE: LOCALES.EN,
    APAC: LOCALES.EN,
  };
  return regionLocaleMap[region] || LOCALES.EN;
}

// =============================================================================
// BROWSER & ENVIRONMENT UTILITIES
// =============================================================================

// Note: Leverage existing functionality at @sm/utils

// Default export for main utilities
export default {
  getVariantClass,
  getRegionForCountry,
  isSupportedLocale,
  getDefaultLocaleForRegion,
};
